import { isDefined } from "@clipboard-health/util-ts";
import { Button, CircularProgress, Stack } from "@mui/material";
import { useEffectivePermissions } from "@src/appV2/Auth/hooks/useEffectivePermissions";
import { useGetFacilityProfile } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { useGetWorkplace } from "@src/appV2/FacilityProfile/api/useGetWorkplace";
import { useTu } from "@src/appV2/i18n/useTu";
import { Text } from "@src/appV2/redesign/components/Text";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";

import { ActiveSettingsSectionContent } from "../components/ActiveSettingsSectionContent";
import { useSettingsSectionConfigs } from "../hooks/useSettingsSectionConfigs";
import { SETTINGS_SECTIONS, type SettingsPermissions, type SettingsSection } from "../types";

// Matches the SettingsContainer projection so the redesigned sections receive
// the same facility-profile fields they get on the standalone Settings page.
const SETTINGS_PROFILE_PROJECTION =
  "requiresLunchBreak rateNegotiation providesRestBreaks maxAllowedWorkConsecutiveHours maxAllowedWorkHoursPerWeek isEarlyClockInEnabled extraWorkedTimeConfig breakPolicies restrictOverworkShiftInvites autoVerifyThresholdInMinutes mapleIntegrationEnabled verificationPreference.preferenceType";

// The parity-only section set the admin drawer composes (D-02). Translation,
// AppInfo, and CalendarDisplay are intentionally excluded.
const ADMIN_PANEL_SECTION_IDS: readonly SettingsSection[] = [
  SETTINGS_SECTIONS.timeAndAttendance,
  SETTINGS_SECTIONS.workerCredentials,
  SETTINGS_SECTIONS.scheduling,
  SETTINGS_SECTIONS.compliance,
  SETTINGS_SECTIONS.rateAndBilling,
  SETTINGS_SECTIONS.jobManagement,
];

const ADMIN_PANEL_PERMISSIONS: SettingsPermissions = {
  isAdmin: true,
  isFacilityAdmin: false,
  hasShiftManagement: false,
  hasInvoices: false,
};

interface AdminWorkplaceSettingsPanelProps {
  workplaceId: string;
}

export function AdminWorkplaceSettingsPanel(props: AdminWorkplaceSettingsPanelProps) {
  const { workplaceId } = props;

  const { tu } = useTu("settings");
  const { hasAdminRole } = useEffectivePermissions();
  const sectionConfigs = useSettingsSectionConfigs();

  const {
    data: facilityProfile,
    refetch: refetchFacilityProfile,
    isError: isFacilityProfileError,
  } = useGetFacilityProfile(workplaceId, SETTINGS_PROFILE_PROJECTION, {
    enabled: isDefined(workplaceId),
  });

  const {
    data: workplace,
    refetch: refetchWorkplace,
    isError: isWorkplaceError,
  } = useGetWorkplace({ workplaceId });

  const hasData = isDefined(facilityProfile) && isDefined(workplace);
  const hasError = isFacilityProfileError || isWorkplaceError;

  // Plain passthrough guard (D-08). The admin session has no signed-profile
  // contract, and useSettingsContractGuard reads session.profile.status
  // unconditionally before its admin short-circuit, which throws when
  // session.profile is undefined in the admin drawer context.
  const guardSettingsAction = (action: () => void) => {
    action();
  };

  const handleSettingsChanged = () => {
    void refetchFacilityProfile();
    void refetchWorkplace();
  };

  const adminPanelSections = sectionConfigs.filter((config) =>
    ADMIN_PANEL_SECTION_IDS.includes(config.id)
  );

  return (
    <BerlinThemeProvider>
      {hasError ? (
        <Stack alignItems="center" spacing={4} sx={{ paddingY: 8 }}>
          <Text>{tu("We couldn't load these settings.")}</Text>
          <Button variant="outlined" onClick={handleSettingsChanged}>
            {tu("Retry")}
          </Button>
        </Stack>
      ) : hasData ? (
        <Stack spacing={4}>
          {adminPanelSections.map((section) => (
            <ActiveSettingsSectionContent
              key={section.id}
              activeSection={section}
              facilityProfile={facilityProfile}
              guardSettingsAction={guardSettingsAction}
              hasAdminRole={hasAdminRole}
              permissions={ADMIN_PANEL_PERMISSIONS}
              workplace={workplace}
              workplaceId={workplaceId}
              onSettingsChanged={handleSettingsChanged}
            />
          ))}
        </Stack>
      ) : (
        <Stack alignItems="center" sx={{ paddingY: 8 }}>
          <CircularProgress />
        </Stack>
      )}
    </BerlinThemeProvider>
  );
}
